"use client";

import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import Link from "next/link";
import React, { useCallback, useEffect, useState } from "react";
import { useParams, usePathname } from "next/navigation";

import { batchItems } from "@/data/MainSidebarItems";
import styles from "./styles.module.scss";
import Components from "@/components";

function BatchSidebar(props) {
  const [sideItems, setSideItems] = useState([]);
  const pathname = usePathname();
  const { batchId } = useParams();

  const totalUnreadCount =
    (props.unreadCount["Tests"] || 0) +
    (props.unreadCount["Questions"] || 0) +
    (props.unreadCount["Updates"] || 0);

  const currentPath = () => {
    let path = pathname.split("/");
    let section = path[path?.length - 1];
    return section?.length > 14 ? "" : section;
  };

  const filterSidebarItems = useCallback(
    (batchItems, entities) => {
      if (!entities) {
        return batchItems;
      }

      const shouldIncludeAnnouncements = ["Tests", "Questions", "Updates"].some(
        (e) => entities.includes(e)
      );

      let res = batchItems.filter((item) => {
        return (
          item.title === "Home" ||
          item.title === "" ||
          // entities.includes("Resources") ||
          entities.includes(item.title) ||
          (item.title === "Announcements" && shouldIncludeAnnouncements)
        );
      });

      setSideItems(res);
    },
    [props.entities]
  );

  useEffect(() => {
    filterSidebarItems(batchItems, props.entities);
  }, [filterSidebarItems]);

  const sideBarContent = (
    <Box className={styles["container"]}>
      <List className={styles["top-list"]}>
        <List>
          {sideItems.map((item, i) => (
            <ListItem
              key={i}
              id={item.id}
              component={Link}
              href={`/batches/${batchId}/${item.path}`}
              className={` ${styles["list-item"]} ${
                currentPath() === item.path ? styles.active : styles.inactive
              }`}
            >
              <ListItemIcon className={styles["item-icon"]}>
                <div className={item.type === "new" ? styles["ms-ripple"] : ""}>
                  <span>{item.icon}</span>
                </div>
                {!props.onOpen ? (
                  <div className={styles["item-text"]}>
                    {item.subtitle === "Contents" ? "Lectures" : item.subtitle}
                  </div>
                ) : null}
              </ListItemIcon>
              {props.onOpen ? (
                <ListItemText>
                  {item.title === "Contents" ? "Lectures" : item.title}
                  {item.title === "Announcements" && totalUnreadCount > 0 && (
                    <span className={styles["notification-count"]}>
                      {totalUnreadCount > 0 ? totalUnreadCount : null}
                    </span>
                  )}
                </ListItemText>
              ) : null}
            </ListItem>
          ))}

          <ListItem
            id={`Compiler-key`}
            target={"_blank"}
            href={"https://compiler.cipherschools.com"}
            component={Link}
            className={` ${styles["list-item"]}`}
          >
            <ListItemIcon className={styles["item-icon"]}>
              <div>
                <span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="30px"
                    height="30px"
                    viewBox="0 0 256 256"
                  >
                    <path
                      fill="var(--text-color)"
                      d="M216 40H40a16 16 0 0 0-16 16v144a16 16 0 0 0 16 16h176a16 16 0 0 0 16-16V56a16 16 0 0 0-16-16M92.8 145.6a8 8 0 1 1-9.6 12.8l-32-24a8 8 0 0 1 0-12.8l32-24a8 8 0 0 1 9.6 12.8L69.33 128Zm58.89-71.4l-32 112a8 8 0 1 1-15.38-4.4l32-112a8 8 0 0 1 15.38 4.4m53.11 60.2l-32 24a8 8 0 0 1-9.6-12.8l23.47-17.6l-23.47-17.6a8 8 0 1 1 9.6-12.8l32 24a8 8 0 0 1 0 12.8"
                    />
                  </svg>
                </span>
              </div>
              {!props.onOpen ? (
                <div className={styles["item-text"]}>{"Compiler"}</div>
              ) : null}
            </ListItemIcon>
            {props.onOpen ? (
              <ListItemText>{"Code Compiler"}</ListItemText>
            ) : null}
          </ListItem>

          <ListItem
            id={`Resume-key`}
            target={"_blank"}
            href={"https://www.cipherschools.com/resume-builder"}
            component={Link}
            className={` ${styles["list-item"]}`}
          >
            <ListItemIcon className={styles["item-icon"]}>
              <div>
                <span>
                  <svg
                    width="30"
                    height="24"
                    viewBox="0 0 28 34"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    id="resume"
                  >
                    <path
                      opacity="0.2"
                      d="M6.125 1.75H17.8238C18.1877 1.74984 18.5477 1.82537 18.8809 1.97179C19.2141 2.1182 19.5132 2.3323 19.7593 2.6005L27.3088 10.829C27.7532 11.3131 27.9998 11.9463 28 12.6035V30.625C28 31.3212 27.7234 31.9889 27.2312 32.4812C26.7389 32.9734 26.0712 33.25 25.375 33.25H6.125C5.42881 33.25 4.76113 32.9734 4.26884 32.4812C3.77656 31.9889 3.5 31.3212 3.5 30.625V4.375C3.5 3.67881 3.77656 3.01113 4.26884 2.51885C4.76113 2.02656 5.42881 1.75 6.125 1.75Z"
                      fill="#FFA103"
                    />
                    <path
                      d="M6.125 20.125C5.89294 20.125 5.67038 20.0328 5.50628 19.8687C5.34219 19.7046 5.25 19.4821 5.25 19.25C5.25 19.0179 5.34219 18.7954 5.50628 18.6313C5.67038 18.4672 5.89294 18.375 6.125 18.375H18.375C18.6071 18.375 18.8296 18.4672 18.9937 18.6313C19.1578 18.7954 19.25 19.0179 19.25 19.25C19.25 19.4821 19.1578 19.7046 18.9937 19.8687C18.8296 20.0328 18.6071 20.125 18.375 20.125H6.125ZM6.125 24.5C5.89294 24.5 5.67038 24.4078 5.50628 24.2437C5.34219 24.0796 5.25 23.8571 5.25 23.625C5.25 23.3929 5.34219 23.1704 5.50628 23.0063C5.67038 22.8422 5.89294 22.75 6.125 22.75H18.375C18.6071 22.75 18.8296 22.8422 18.9937 23.0063C19.1578 23.1704 19.25 23.3929 19.25 23.625C19.25 23.8571 19.1578 24.0796 18.9937 24.2437C18.8296 24.4078 18.6071 24.5 18.375 24.5H6.125Z"
                      fill="currentColor"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M14.3238 2.44548e-07H2.625C1.92881 2.44548e-07 1.26113 0.276562 0.768845 0.768845C0.276562 1.26113 0 1.92881 0 2.625V28.875C0 29.5712 0.276562 30.2389 0.768845 30.7312C1.26113 31.2234 1.92881 31.5 2.625 31.5H21.875C22.5712 31.5 23.2389 31.2234 23.7312 30.7312C24.2234 30.2389 24.5 29.5712 24.5 28.875V10.8535C24.4998 10.1963 24.2532 9.56312 23.8088 9.079L16.2593 0.8505C16.0132 0.582305 15.7141 0.368201 15.3809 0.221786C15.0477 0.0753703 14.6877 -0.000156846 14.3238 2.44548e-07ZM1.75 2.625C1.75 2.39294 1.84219 2.17038 2.00628 2.00628C2.17038 1.84219 2.39294 1.75 2.625 1.75H14.3238C14.4452 1.74987 14.5653 1.775 14.6765 1.82381C14.7876 1.87262 14.8874 1.94403 14.9695 2.0335L22.519 10.262C22.6674 10.4233 22.7498 10.6344 22.75 10.8535V28.875C22.75 29.1071 22.6578 29.3296 22.4937 29.4937C22.3296 29.6578 22.1071 29.75 21.875 29.75H2.625C2.39294 29.75 2.17038 29.6578 2.00628 29.4937C1.84219 29.3296 1.75 29.1071 1.75 28.875V2.625Z"
                      fill="currentColor"
                    />
                    <path
                      d="M14.875 1.9248V10.1498H23.1"
                      stroke="currentColor"
                      strokeWidth="0.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8.98453 8.98265C9.23444 8.9903 9.48333 8.94769 9.71646 8.85734C9.94958 8.76699 10.1622 8.63074 10.3417 8.45667C10.5211 8.28261 10.6638 8.07427 10.7613 7.84402C10.8587 7.61377 10.9089 7.3663 10.9089 7.11627C10.9089 6.86625 10.8587 6.61878 10.7613 6.38853C10.6638 6.15827 10.5211 5.94994 10.3417 5.77587C10.1622 5.60181 9.94958 5.46556 9.71646 5.37521C9.48333 5.28486 9.23444 5.24225 8.98453 5.2499C8.49943 5.26475 8.03917 5.4679 7.70129 5.81629C7.3634 6.16469 7.17444 6.63095 7.17444 7.11627C7.17444 7.6016 7.3634 8.06786 7.70129 8.41625C8.03917 8.76465 8.49943 8.96779 8.98453 8.98265Z"
                      fill="currentColor"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M12.7155 13.0272C12.7155 11.0427 11.0443 9.60596 8.98275 9.60596C6.92125 9.60596 5.25 11.041 5.25 13.0272V13.9617C5.25046 14.1266 5.3163 14.2846 5.43309 14.4011C5.54987 14.5176 5.70807 14.583 5.873 14.583H12.0943C12.2589 14.5825 12.4166 14.5169 12.533 14.4005C12.6494 14.2841 12.715 14.1263 12.7155 13.9617V13.0272Z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
              </div>
              {!props.onOpen ? (
                <div className={styles["item-text"]}>{"Resume"}</div>
              ) : null}
            </ListItemIcon>
            {props.onOpen ? <ListItemText>{"Resume"}</ListItemText> : null}
          </ListItem>

          <ListItem
            id={`Support`}
            href={`/batches/${batchId}/support`}
            component={Link}
            className={` ${styles["list-item"]} ${
              currentPath() === "support" ? styles.active : styles.inactive
            }`}
          >
            <ListItemIcon className={styles["item-icon"]}>
              <div>
                <span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="currentColor"
                      d="M12 2C6.486 2 2 6.486 2 12v4.143C2 17.167 2.897 18 4 18h1a1 1 0 0 0 1-1v-5.143a1 1 0 0 0-1-1h-.908C4.648 6.987 7.978 4 12 4s7.352 2.987 7.908 6.857H19a1 1 0 0 0-1 1V18c0 1.103-.897 2-2 2h-2v-1h-4v3h6c2.206 0 4-1.794 4-4c1.103 0 2-.833 2-1.857V12c0-5.514-4.486-10-10-10"
                    />
                  </svg>
                </span>
              </div>
              {!props.onOpen ? (
                <div className={styles["item-text"]}>
                  {"Support"}{" "}
                  <span className={styles["notification-count"]}>
                    {props.supportUnread > 0 ? props.supportUnread : null}
                  </span>
                </div>
              ) : null}
            </ListItemIcon>
            {props.onOpen ? (
              <ListItemText>
                {"Support"}{" "}
                {props.supportUnread > 0 ? (
                  <span className={styles["notification-count"]}>
                    {props.supportUnread > 0 ? props.supportUnread : null}
                  </span>
                ) : (
                  ""
                )}
              </ListItemText>
            ) : null}
          </ListItem>
        </List>
      </List>
      <ListItem
        className={styles["list-item"]}
        href={"/batches"}
        component={Link}
      >
        <Components.Tooltip title="Courses" placement="right" arrow>
          <ListItemIcon className={styles["item-icon"]}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="27px"
              height="27px"
              viewBox="0 0 24 24"
            >
              <path
                fill="currentColor"
                d="M12 3L1 9l11 6l9-4.91V17h2V9M5 13.18v4L12 21l7-3.82v-4L12 17z"
              />
            </svg>
          </ListItemIcon>
        </Components.Tooltip>
        {props.onOpen ? <ListItemText>My Batches</ListItemText> : null}
      </ListItem>
    </Box>
  );

  return (
    <article className={styles["main"]}>
      <Drawer
        variant="permanent"
        classes={{
          paper: `${styles[`drawer-${props.onOpen ? "open" : "close"}`]} ${props.className}`,
        }}
        className={styles.drawer}
        open
      >
        {sideBarContent}
      </Drawer>
    </article>
  );
}

export default BatchSidebar;
